import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { useInView } from 'react-intersection-observer'
import { DotsLoader, Box, Flex } from '@elparking/components'
import matchWords from 'commons/js/util/matchWords'
import { useApi } from '@elparking/utils'
import { getViatParkingsByCountry } from 'commons/js/api'

import List from '../List'
import Search from '../Search'
import { trackTealiumEvent, ViatShopEvents } from 'commons/js/services/analytics'
const mediaScreenMobile = props => `@media screen and (max-width: ${props.theme.screens.medium})`

const MIN_LENGTH_SEARCH = 3
const EMPTY_ITEMS = []

const OverflowList = styled.div`
    overflow-y: auto;
    height: calc(88px * 4);

    ${mediaScreenMobile} {
        overflow-y: none;
        height: auto;
    }
`

function makeItemsFilter (search) {
    return (item) =>
        search.length === 0 ||
        search.length < MIN_LENGTH_SEARCH ||
        matchWords(search, item.province.name)
}

function ListSearcher ({ countryId, getViatParkingsByCountry }) {
    const {
        loading,
        response: { result },
        sendRequest: requestGetViatParkingsByCountry,
    } = useApi(getViatParkingsByCountry)
    const [filteredItems, setFilteredItems] = useState([])
    const items = result || EMPTY_ITEMS

    function onChange (search) {
        setFilteredItems(items.filter(makeItemsFilter(search)))
    }

    const onChangeCallback = useCallback(onChange, [items])

    useEffect(() => {
        setFilteredItems(items)
    }, [items])

    useEffect(() => {
        requestGetViatParkingsByCountry({ countryId })
    }, [requestGetViatParkingsByCountry, countryId])

    return (
        <>
            <Box pb='32px' pt='8px' w={['100%', '60%']}>
                <Search onChange={onChangeCallback} />
            </Box>
            <OverflowList>
                {loading
                    ? <Flex justifyContent='center' w='100%'><DotsLoader /></Flex>
                    : <List items={filteredItems} countryId={countryId} />}
            </OverflowList>
        </>
    )
}

ListSearcher.propTypes = {
    countryId: PropTypes.string,
    getViatParkingsByCountry: PropTypes.func,
}

ListSearcher.defaultProps = {
    getViatParkingsByCountry,
}

const InViewListSearcher = ({ initialInView, ...restProps }) => {
    const { ref, inView } = useInView({
        initialInView,
        triggerOnce: true,
        threshold: 0,
    })

    useEffect(() => {
        if (inView) {
            trackTealiumEvent(ViatShopEvents.parkingsByCountryView)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView])
    return (
        <div ref={ref}>
            {inView && <ListSearcher {...restProps} />}
        </div>
    )
}

InViewListSearcher.propTypes = {
    initialInView: PropTypes.bool,
}

InViewListSearcher.defaultProps = {
    initialInView: false,
}

export default InViewListSearcher
